import React, { useState, useEffect } from 'react';
import {
  IndianRupee,
  Clock,
  Calendar,
  Building2,
  TrendingUp
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { incentivesAPI } from '../../services/api';
import { usePrivacy } from '../../contexts/PrivacyContext';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const [year, month] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const UpcomingInvoices = () => {
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const [loading, setLoading] = useState(true);
  const [upcomingReceivables, setUpcomingReceivables] = useState([]);
  const [currentDate] = useState(new Date());

  useEffect(() => {
    const fetchUpcoming = async () => {
      try {
        setLoading(true);
        const response = await incentivesAPI.listReceivables();
        const records = response.data || [];

        // Filter for future dates (Asia/Kolkata calendar month)
        const now = new Date();
        const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
        const currentMonthStr = `${istNow.getFullYear()}-${String(istNow.getMonth() + 1).padStart(2, '0')}`;

        const future = records.filter(rec => (rec.period_month || '') > currentMonthStr);
        future.sort((a, b) => a.period_month.localeCompare(b.period_month));

        setUpcomingReceivables(future);
      } catch (error) {
        console.error('Failed to fetch upcoming invoices:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUpcoming();
  }, []);

  if (loading) {
    return <div className="p-4 text-center text-slate-500">Loading upcoming invoices...</div>;
  }

  return (
    <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                    <p className="text-sm font-medium text-blue-600">Total Future Revenue</p>
                    <p className="text-2xl font-bold text-blue-700">
                        {formatCurrency(upcomingReceivables.reduce((sum, i) => sum + i.amount, 0))}
                    </p>
                </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-6">
                    <p className="text-sm font-medium text-purple-600">Upcoming Invoices</p>
                    <p className="text-2xl font-bold text-purple-700">{upcomingReceivables.length}</p>
                </CardContent>
            </Card>
        </div>

        <Card className="bg-white border-slate-200">
            <CardContent className="p-0">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Due Month</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead>Source Type</TableHead>
                            <TableHead>Expected Amount</TableHead>
                            <TableHead>Description</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {upcomingReceivables.length > 0 ? (
                            upcomingReceivables.map(rcv => (
                                <TableRow key={rcv.id}>
                                    <TableCell className="font-medium">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-slate-400" />
                                            {formatDate(rcv.period_month)}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Building2 className="w-4 h-4 text-slate-400" />
                                            {rcv.client_name}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline">{rcv.source_type}</Badge>
                                    </TableCell>
                                    <TableCell className="font-bold text-emerald-600">
                                        {formatCurrency(rcv.amount)}
                                    </TableCell>
                                    <TableCell className="text-xs text-slate-500 max-w-xs truncate">
                                        {rcv.description}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                                    No upcoming invoices scheduled
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    </div>
  );
};

export default UpcomingInvoices;
